import { useCallback, useRef, useState } from "react";
import { useProvetStatic } from "../context";
import { ProvetBookingFlowState } from "./provetBookingFlowReducer";
import { TrackingState } from "./useProvetTracking";

interface UseBookingCancellationOptions {
  bookingFlowState: ProvetBookingFlowState;
  tracking: TrackingState;
  modals: {
    cancelBooking: {
      open: () => void;
      close: () => void;
    };
    cancelSuccess: {
      open: (details: any) => void;
      close: () => void;
    };
    cancelFailure: {
      open: (details: any) => void;
      close: () => void;
    };
  };
  resetBooking: () => Promise<void>;
}

interface BookingCancellationHandlers {
  cancelBooking: () => void;
  confirmCancelBooking: () => Promise<void>;
  closeCancelBookingModal: () => void;
  closeCancelSuccessModal: () => Promise<void>;
  closeCancelFailureModal: () => void;
  /** Whether the cancellation has been sent and the server hasn't answered yet */
  isCancelling: boolean;
}

/**
 * Hook for managing booking cancellation flow.
 * Handles cancel booking modal, API cancellation, and success/failure modals.
 */
export const useBookingCancellation = ({
  bookingFlowState,
  tracking,
  modals,
  resetBooking
}: UseBookingCancellationOptions): BookingCancellationHandlers => {
  const { api } = useProvetStatic();
  const [isCancelling, setIsCancelling] = useState(false);
  // Blocks a second cancellation before the disabled button has rendered
  const cancellingRef = useRef(false);

  const cancelBooking = () => {
    modals.cancelBooking.open();
  };

  const confirmCancelBooking = useCallback(async () => {
    if (cancellingRef.current) {
      return;
    }

    // Store cancellation details before attempting cancellation
    const cancellationDetails = {
      appointmentTime:
        bookingFlowState.formData?.selectedAppointmentTime?.availableTime || "",
      appointmentDate:
        bookingFlowState.formData?.selectedAppointmentTime?.availableTime || "",
      departmentName: bookingFlowState.formData?.selectedDepartment?.name || "",
      departmentPhone: bookingFlowState.formData?.selectedDepartment?.address_info?.phone,
      departmentEmail: bookingFlowState.formData?.selectedDepartment?.email
    };

    // Without the booking id it can't be cancelled online: show how to contact the clinic
    if (!tracking.bookingId) {
      console.warn("Cannot cancel booking: No booking ID available");
      modals.cancelBooking.close();
      modals.cancelFailure.open(cancellationDetails);
      return;
    }

    // The dialog stays open and shows progress until the server has answered
    cancellingRef.current = true;
    setIsCancelling(true);

    let success = false;

    try {
      success = await api.cancelBooking(
        tracking.bookingId,
        bookingFlowState.cancelToken ?? ""
      );
    } catch (error) {
      console.error("Error cancelling booking:", error);
    } finally {
      cancellingRef.current = false;
      setIsCancelling(false);
    }

    modals.cancelBooking.close();

    if (success) {
      modals.cancelSuccess.open(cancellationDetails);
    } else {
      modals.cancelFailure.open(cancellationDetails);
    }
  }, [
    tracking.bookingId,
    api,
    modals,
    bookingFlowState.formData,
    bookingFlowState.cancelToken
  ]);

  const closeCancelSuccessModal = useCallback(async () => {
    modals.cancelSuccess.close();
    // Reset the booking flow to start over
    await resetBooking();
  }, [modals.cancelSuccess, resetBooking]);

  // The dialog can't be closed while the cancellation is being sent
  const closeCancelBookingModal = useCallback(() => {
    if (cancellingRef.current) {
      return;
    }

    modals.cancelBooking.close();
  }, [modals.cancelBooking]);

  // The booking probably still exists, so stay on the confirmation page
  const closeCancelFailureModal = useCallback(() => {
    modals.cancelFailure.close();
  }, [modals.cancelFailure]);

  return {
    cancelBooking,
    confirmCancelBooking,
    closeCancelBookingModal,
    closeCancelSuccessModal,
    closeCancelFailureModal,
    isCancelling
  };
};
